import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Users, Calendar, Plus, Settings, Crown, Heart, Sparkles, ArrowRight, Home } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import NavigationBar from './NavigationBar';
import { useNavigate } from 'react-router-dom';
import { FamilyManagement } from './FamilyManagement';
import { FamilyEventTemplates } from './FamilyEventTemplates';
import RecurringEventsManager from './RecurringEventsManager';
import { toast } from 'sonner';

interface FamilyGroup {
  id: string;
  name: string;
  description: string;
  owner_id: string;
  created_at: string;
  member_count: number;
  user_role: 'owner' | 'member' | 'admin';
}

interface FamilyEvent {
  id: string;
  name: string;
  description: string;
  event_type: string;
  is_active: boolean;
  deadline: string | null;
  created_at: string;
  family_group_id: string;
  host_name: string;
}

export const FamilyDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [familyGroups, setFamilyGroups] = useState<FamilyGroup[]>([]);
  const [familyEvents, setFamilyEvents] = useState<FamilyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');

  useEffect(() => {
    if (user) {
      loadFamilyData();
    }
  }, [user]);

  const loadFamilyData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      console.log('Loading family data for user:', user.id);

      // First, update any pending memberships for this user's email
      await supabase
        .from('family_memberships')
        .update({ 
          user_id: user.id,
          status: 'active',
          joined_at: new Date().toISOString()
        })
        .eq('email', user.email)
        .eq('status', 'pending')
        .is('user_id', null);

      // Get family groups owned by user
      console.log('Querying owned groups for user_id:', user.id);
      const { data: ownedGroups, error: ownedError } = await supabase
        .from('family_groups')
        .select('*')
        .eq('owner_id', user.id);

      console.log('Owned groups result:', { ownedGroups, ownedError });
      if (ownedError) {
        console.error('Error loading owned family groups:', ownedError);
        throw ownedError;
      }

      // Get family groups where user is a member by user_id
      console.log('Querying member groups by user_id:', user.id);
      const { data: memberGroups, error: memberError } = await supabase
        .from('family_groups')
        .select(`
          *,
          family_memberships!inner(
            role,
            status
          )
        `)
        .eq('family_memberships.user_id', user.id)
        .in('family_memberships.status', ['active', 'pending']);

      console.log('Member groups by user_id result:', { memberGroups, memberError });
      if (memberError) {
        console.error('Error loading member family groups:', memberError);
        throw memberError;
      }

      // Also get groups where user is member by email
      console.log('Querying member groups by email:', user.email);
      const { data: emailMemberGroups, error: emailMemberError } = await supabase
        .from('family_groups')
        .select(`
          *,
          family_memberships!inner(
            role,
            status
          )
        `)
        .eq('family_memberships.email', user.email)
        .in('family_memberships.status', ['active', 'pending']);

      console.log('Member groups by email result:', { emailMemberGroups, emailMemberError });
      if (emailMemberError) {
        console.error('Error loading email member family groups:', emailMemberError);
        throw emailMemberError;
      }

      // Combine and deduplicate groups
      const allGroups = [];
      const seenIds = new Set();

      // Add owned groups
      (ownedGroups || []).forEach(group => {
        if (!seenIds.has(group.id)) {
          seenIds.add(group.id);
          allGroups.push({ ...group, user_role: 'owner' as const });
        }
      });

      // Add member groups
      (memberGroups || []).forEach(group => {
        if (!seenIds.has(group.id)) {
          seenIds.add(group.id);
          const membership = Array.isArray(group.family_memberships) 
            ? group.family_memberships[0] 
            : group.family_memberships;
          allGroups.push({ 
            ...group, 
            user_role: membership?.role || 'member' as const 
          });
        }
      });

      // Add email member groups
      (emailMemberGroups || []).forEach(group => {
        if (!seenIds.has(group.id)) {
          seenIds.add(group.id);
          const membership = Array.isArray(group.family_memberships) 
            ? group.family_memberships[0] 
            : group.family_memberships;
          allGroups.push({ 
            ...group, 
            user_role: membership?.role || 'member' as const 
          });
        }
      });

      // Get member counts for each group
      const groupsWithCount = await Promise.all(
        allGroups.map(async (group) => {
          const { count } = await supabase
            .from('family_memberships')
            .select('*', { count: 'exact', head: true })
            .eq('family_group_id', group.id)
            .eq('status', 'active');

          return {
            ...group,
            member_count: (count || 0) + 1 // +1 for the owner
          };
        })
      );

      console.log('Loaded all family groups:', groupsWithCount);
      setFamilyGroups(groupsWithCount.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ));

      // Load family events for all accessible groups
      const groupIds = groupsWithCount.map(g => g.id);
      if (groupIds.length > 0) {
        const { data: events, error: eventsError } = await supabase
          .from('events')
          .select('*')
          .in('family_group_id', groupIds)
          .order('created_at', { ascending: false });

        if (eventsError) {
          console.error('Error loading family events:', eventsError);
        } else {
          setFamilyEvents(events || []);
        }
      } else {
        setFamilyEvents([]);
      }
    } catch (error) {
      console.error('Error loading family data:', error);
      toast.error('Failed to load family data');
    } finally {
      setLoading(false);
    }
  };

  const createFamilyGroup = async () => {
    if (!user || !newGroupName.trim()) return;

    try {
      console.log('Creating family group:', newGroupName.trim());
      const { data, error } = await supabase
        .from('family_groups')
        .insert({
          name: newGroupName.trim(),
          owner_id: user.id,
          description: newGroupDescription.trim() || `${newGroupName.trim()} family group`
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating family group:', error);
        throw error;
      }

      console.log('Family group created successfully:', data);

      toast.success('Family group created successfully!');
      setIsCreateDialogOpen(false);
      setNewGroupName('');
      setNewGroupDescription('');
      loadFamilyData();
    } catch (error) {
      console.error('Error creating family group:', error);
      toast.error('Failed to create family group');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary border-t-transparent mx-auto" />
          <p className="text-muted-foreground">Loading your family dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <NavigationBar />
      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary/5 via-primary/10 to-accent/5 p-8 mb-8 animate-fade-in">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-accent/20 opacity-50 sm:opacity-40 md:opacity-50" />
          <div className="absolute top-4 right-4 opacity-20">
            <Heart className="h-16 w-16 text-primary" />
          </div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-primary/10">
                  <Home className="h-6 w-6 text-primary" />
                </div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Family Dashboard
                </h1>
              </div>
              <p className="text-lg text-muted-foreground">
                Bringing families together, one event at a time
              </p>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Sparkles className="h-4 w-4" />
                <span>{familyGroups.length} family groups • Organized with love</span>
              </div>
            </div>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button size="lg" className="hover-scale shadow-lg">
                  <Plus className="h-5 w-5 mr-2" />
                  Create Family Group
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] animate-scale-in">
                <DialogHeader className="text-center space-y-3">
                  <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <DialogTitle className="text-2xl">Create Your Family Group</DialogTitle>
                  <DialogDescription className="text-base">
                    Start organizing amazing family events and create lasting memories together.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium">
                      Family Group Name
                    </Label>
                    <Input
                      id="name"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      placeholder="e.g., The Johnson Family"
                      maxLength={50}
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description" className="text-sm font-medium">
                      Description <span className="text-muted-foreground">(optional)</span>
                    </Label>
                    <Textarea
                      id="description"
                      value={newGroupDescription}
                      onChange={(e) => setNewGroupDescription(e.target.value)}
                      placeholder="Tell us about your family group..."
                      maxLength={200}
                      rows={3}
                      className="resize-none"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsCreateDialogOpen(false)}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={createFamilyGroup}
                    disabled={!newGroupName.trim()}
                    className="w-full sm:w-auto"
                  >
                    <Heart className="h-4 w-4 mr-2" />
                    Create Group
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Navigation Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <div className="flex justify-center">
            <TabsList className="grid grid-cols-5 h-12 p-1 bg-muted/50 backdrop-blur-sm">
              <TabsTrigger value="overview" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
                Overview
              </TabsTrigger>
              <TabsTrigger value="events" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
                Events
              </TabsTrigger>
              <TabsTrigger value="templates" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
                Templates
              </TabsTrigger>
              <TabsTrigger value="recurring" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
                Recurring
              </TabsTrigger>
              <TabsTrigger value="manage" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
                Manage
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-8 animate-fade-in">
            {familyGroups.length > 0 ? (
              <>
                {/* Quick Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="border-0 bg-gradient-to-br from-primary/5 to-primary/10">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Family Groups</p>
                          <p className="text-3xl font-bold text-primary">{familyGroups.length}</p>
                        </div>
                        <div className="p-3 bg-primary/10 rounded-full">
                          <Users className="h-6 w-6 text-primary" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-0 bg-gradient-to-br from-accent/5 to-accent/10">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Total Members</p>
                          <p className="text-3xl font-bold text-accent">
                            {familyGroups.reduce((sum, group) => sum + group.member_count, 0)}
                          </p>
                        </div>
                        <div className="p-3 bg-accent/10 rounded-full">
                          <Heart className="h-6 w-6 text-accent" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-0 bg-gradient-to-br from-muted/5 to-muted/10">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Events Created</p>
                          <p className="text-3xl font-bold">{familyEvents.length}</p>
                        </div>
                        <div className="p-3 bg-muted/20 rounded-full">
                          <Calendar className="h-6 w-6" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Family Groups Grid */}
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-semibold">Your Family Groups</h2>
                    <Button variant="ghost" onClick={() => setActiveTab('manage')}>
                      Manage All
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {familyGroups.map((group, index) => (
                      <Card 
                        key={group.id} 
                        className="group hover-scale border-0 shadow-md hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-background to-muted/5"
                        style={{ 
                          animationDelay: `${index * 100}ms`,
                          animation: 'fade-in 0.5s ease-out forwards'
                        }}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <CardTitle className="flex items-center gap-3 text-lg">
                                <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                                  <Users className="h-5 w-5 text-primary" />
                                </div>
                                {group.name}
                              </CardTitle>
                              <div className="flex items-center gap-2">
                                <Badge 
                                  variant={group.user_role === 'owner' ? 'secondary' : 'default'} 
                                  className="text-xs"
                                >
                                  {group.user_role === 'owner' && <Crown className="h-3 w-3 mr-1" />}
                                  {group.user_role === 'admin' && <Settings className="h-3 w-3 mr-1" />}
                                  {group.user_role === 'member' && <Users className="h-3 w-3 mr-1" />}
                                  {group.user_role.charAt(0).toUpperCase() + group.user_role.slice(1)}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {group.member_count} member{group.member_count !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <CardDescription className="text-sm leading-relaxed">
                            {group.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <Separator className="mb-4" />
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-muted-foreground">
                              Created {new Date(group.created_at).toLocaleDateString()}
                            </div>
                             <Button 
                               variant="ghost" 
                               size="sm" 
                               onClick={() => navigate('/create-event', {
                                 state: {
                                   familyGroupId: group.id,
                                   familyGroupName: group.name
                                 }
                               })}
                               className="h-8 px-3 hover:bg-primary/10"
                             >
                               Create Event
                             </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <Card className="border-0 bg-gradient-to-br from-muted/5 to-muted/10 animate-fade-in">
                <CardContent className="text-center py-16">
                  <div className="space-y-6">
                    <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                      <Users className="h-10 w-10 text-primary" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-2xl font-semibold">Welcome to Your Family Dashboard!</h3>
                      <p className="text-muted-foreground max-w-md mx-auto">
                        Start your journey by creating your first family group. Bring everyone together 
                        and make event planning a breeze.
                      </p>
                    </div>
                    <Button 
                      size="lg" 
                      onClick={() => setIsCreateDialogOpen(true)}
                      className="hover-scale shadow-lg"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Create Your First Family Group
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Events Section */}
            {familyEvents.length > 0 && (
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-accent/10">
                      <Calendar className="h-5 w-5 text-accent" />
                    </div>
                    Recent Family Events
                  </CardTitle>
                  <CardDescription>Latest activities from your family groups</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {familyEvents.slice(0, 5).map((event, index) => (
                      <div 
                        key={event.id} 
                        className="flex items-center justify-between py-4 border-b last:border-b-0 animate-fade-in"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div className="space-y-1">
                          <h4 className="font-medium">{event.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            by {event.host_name} • {new Date(event.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge variant={event.is_active ? 'default' : 'secondary'}>
                          {event.is_active ? 'Active' : 'Closed'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="animate-fade-in">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Calendar className="h-5 w-5 text-primary" />
                  </div>
                  Family Events
                </CardTitle>
                <CardDescription>All events organized by your family groups</CardDescription>
              </CardHeader>
              <CardContent>
                {familyEvents.length > 0 ? (
                  <div className="space-y-4">
                    {familyEvents.map((event, index) => (
                      <div 
                        key={event.id} 
                        className="flex items-center justify-between p-6 border rounded-xl hover:bg-muted/30 transition-colors animate-fade-in"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div className="flex-1 space-y-2">
                          <h4 className="font-semibold text-lg">{event.name}</h4>
                          <p className="text-muted-foreground">{event.description}</p>
                          <div className="flex items-center gap-6 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              {event.host_name}
                            </span>
                            <span>Type: {event.event_type}</span>
                            {event.deadline && (
                              <span>Deadline: {new Date(event.deadline).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant={event.is_active ? 'default' : 'secondary'} className="px-3 py-1">
                            {event.is_active ? 'Active' : 'Closed'}
                          </Badge>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="hover-scale"
                            onClick={() => setActiveTab('templates')}
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="space-y-4">
                      <div className="mx-auto w-16 h-16 rounded-full bg-muted/20 flex items-center justify-center">
                        <Calendar className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold">No Events Yet</h3>
                        <p className="text-muted-foreground">
                          Create your first family event to get started!
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="animate-fade-in">
            <FamilyEventTemplates />
          </TabsContent>

          {/* Recurring Tab */}
          <TabsContent value="recurring" className="animate-fade-in">
            <RecurringEventsManager />
          </TabsContent>

          {/* Manage Tab */}
          <TabsContent value="manage" className="animate-fade-in">
            {familyGroups.length > 0 ? (
              <FamilyManagement familyGroups={familyGroups} onUpdate={loadFamilyData} />
            ) : (
              <Card className="border-0 shadow-md">
                <CardContent className="text-center py-16">
                  <div className="space-y-4">
                    <div className="mx-auto w-16 h-16 rounded-full bg-muted/20 flex items-center justify-center">
                      <Settings className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold">No Family Groups to Manage</h3>
                      <p className="text-muted-foreground">
                        Create a family group first to manage members and settings
                      </p>
                    </div>
                    <Button onClick={() => setIsCreateDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Family Group
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};